import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '@/components';
import { useModalClose, useBodyScrollLock } from '@/common/hooks';
import {
  getGoogleWorkspaceStatus,
  getGoogleWorkspaceAuthUrl,
  getGoogleWorkspacePickerSession,
  exportGoogleWorkspaceSheet,
} from '../api';
import { GoogleSheetsIcon } from './GoogleSheetSelector';
import {
  loadGoogleApiScript,
  openGoogleAuthWindow,
  openSpreadsheetPicker,
  openSpreadsheetUrl,
  parseGoogleWorkspaceError,
  pickerSessionErrorMessage,
} from '../utils/googleWorkspaceUtils';

const NATIVE_SHEET_MIME = 'application/vnd.google-apps.spreadsheet';

const GW_CALLBACK_URL = `${typeof window !== 'undefined' ? window.location.origin : ''}/integrations/google-workspace/callback`;

function resolveCompanyId(propCompanyId) {
  if (propCompanyId) return propCompanyId;
  let id =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!id) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        id = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore
    }
  }
  return id;
}

export function CRMExportModal({
  isOpen,
  onClose,
  onExport,
  isExporting,
  filteredCount,
  hasActiveFilters = false,
  companyId,
  getExportFilterParams,
}) {
  const activeCompanyId = resolveCompanyId(companyId);

  // Export Target: 'csv' | 'google_sheets'
  const [exportTarget, setExportTarget] = useState('csv');

  // CSV options
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeFollowups, setIncludeFollowups] = useState(false);

  // Google Sheets options
  // mode: 'new' (Create new Google Spreadsheet) | 'existing' (Add to existing spreadsheet)
  const [sheetMode, setSheetMode] = useState('new');
  const [sheetTitle, setSheetTitle] = useState(() => {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'long' });
    const year = now.getFullYear();
    return `Contacts Export - ${month} ${year}`;
  });
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isPickerLoading, setIsPickerLoading] = useState(false);
  const [googleExportResult, setGoogleExportResult] = useState(null);
  const [apiErrorMessage, setApiErrorMessage] = useState(null);

  useBodyScrollLock(isOpen);

  // Connection query for Google Workspace
  const {
    data: gwStatus,
    isLoading: isGwStatusLoading,
    refetch: refetchGwStatus,
  } = useQuery({
    queryKey: ['google-workspace-status', activeCompanyId],
    queryFn: () => getGoogleWorkspaceStatus(activeCompanyId),
    enabled: isOpen && !!activeCompanyId,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    const onAuthDone = (event) => {
      if (event.data?.type !== 'gw-auth-done') return;
      if (event.data.status === 'error') {
        toast.error(event.data.message || 'Google sign-in did not finish.');
        return;
      }
      if (event.data.status !== 'success') return;
      refetchGwStatus();
    };
    const onFocus = () => {
      refetchGwStatus();
    };
    window.addEventListener('message', onAuthDone);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('message', onAuthDone);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchGwStatus]);

  const isGwConnected = Boolean(gwStatus?.is_connected);

  useEffect(() => {
    if (!isOpen) {
      setIncludeNotes(false);
      setIncludeFollowups(false);
      setExportTarget('csv');
      setSheetMode('new');
      setGoogleExportResult(null);
      setApiErrorMessage(null);
    }
  }, [isOpen]);

  const { mutate: handleGoogleExport, isPending: isGoogleExporting } =
    useMutation({
      mutationFn: exportGoogleWorkspaceSheet,
      onSuccess: (data) => {
        setGoogleExportResult(data);
        setApiErrorMessage(null);
        if (data?.spreadsheet_url) openSpreadsheetUrl(data.spreadsheet_url);
        toast.success(
          `Exported ${data.row_count ?? ''} contacts to Google Sheets!`,
        );
      },
      onError: (err) => {
        const parsed = parseGoogleWorkspaceError(err);
        setApiErrorMessage(parsed.message);
        toast.error(parsed.message);
        if (parsed.isAuthError) {
          refetchGwStatus();
        }
      },
    });

  const handleConnectGoogle = async () => {
    if (!activeCompanyId) {
      toast.error('Company ID is missing.');
      return;
    }
    setIsConnectingGoogle(true);
    try {
      const res = await getGoogleWorkspaceAuthUrl(
        activeCompanyId,
        GW_CALLBACK_URL,
      );
      const authUrl = res?.auth_url ?? res?.url;
      if (!authUrl) {
        toast.error('Could not retrieve Google authorization URL.');
        setIsConnectingGoogle(false);
        return;
      }
      sessionStorage.setItem('gw_company_id', String(activeCompanyId));
      localStorage.setItem('gw_company_id', String(activeCompanyId));
      openGoogleAuthWindow(authUrl);
      setIsConnectingGoogle(false);
    } catch (err) {
      const parsed = parseGoogleWorkspaceError(err);
      toast.error(parsed.message || 'Failed to initiate Google sign-in.');
      setIsConnectingGoogle(false);
    }
  };

  const googleExportExtras = () => {
    const filterParams = getExportFilterParams?.() ?? {};
    const payload = {
      include_notes: includeNotes,
      include_followups: includeFollowups,
    };
    if (filterParams && Object.keys(filterParams).length > 0) {
      payload.filter_params = filterParams;
    }
    return payload;
  };

  const handleExport = () => {
    if (exportTarget === 'csv') {
      onExport({
        include_notes: includeNotes,
        include_followups: includeFollowups,
      });
    } else {
      setApiErrorMessage(null);
      if (sheetMode === 'new') {
        const title = sheetTitle.trim();
        handleGoogleExport({
          companyId: activeCompanyId,
          payload: {
            ...googleExportExtras(),
            ...(title ? { title } : {}),
          },
        });
      } else {
        openExistingSheetPicker();
      }
    }
  };

  const openExistingSheetPicker = async () => {
    if (!activeCompanyId) {
      toast.error('Company ID is missing.');
      return;
    }
    const apiKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
    if (!apiKey) {
      toast.error('VITE_GOOGLE_PICKER_API_KEY is missing.');
      return;
    }
    setIsPickerLoading(true);
    setApiErrorMessage(null);
    try {
      const session = await getGoogleWorkspacePickerSession(activeCompanyId);
      await loadGoogleApiScript();
      await openSpreadsheetPicker({
        accessToken: session.access_token,
        appId: session.app_id,
        apiKey,
        mimeTypes: NATIVE_SHEET_MIME,
        onPicked: (file) => {
          handleGoogleExport({
            companyId: activeCompanyId,
            payload: {
              spreadsheet_id: file.id,
              sheet_name: 'Sheet1',
              ...googleExportExtras(),
            },
          });
        },
      });
    } catch (err) {
      const sessionMessage = pickerSessionErrorMessage(err);
      if (sessionMessage) {
        setApiErrorMessage(sessionMessage);
        toast.error(sessionMessage);
        refetchGwStatus();
      } else {
        const parsed = parseGoogleWorkspaceError(err);
        setApiErrorMessage(parsed.message);
        toast.error(parsed.message || 'Could not open Google Picker.');
      }
    } finally {
      setIsPickerLoading(false);
    }
  };

  const modalRef = useModalClose({
    isOpen,
    onClose,
    isDirty: false,
  });

  if (!isOpen) return null;

  const isExportActive = isExporting || isGoogleExporting || isPickerLoading;

  const canSubmitGoogle =
    isGwConnected && !isGoogleExporting && !isPickerLoading;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        ref={modalRef}
        className="lead-export-modal w-full max-w-[540px] max-h-[calc(100vh-2rem)] sm:max-h-[90vh] bg-card-background border border-border-default rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border-default shrink-0">
          <div>
            <h2 className="text-h6 sm:text-h5 font-bold text-text-primary tracking-tight">
              Export Contacts
            </h2>
            <p className="text-body-sm text-text-secondary mt-0.5 font-medium">
              {hasActiveFilters
                ? `Exports the ${filteredCount ?? 0} contact(s) matching your current filters.`
                : 'Exports all contacts. Apply list filters first to export a subset.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
            aria-label="Close export modal"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.75"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex flex-col gap-5">
          {/* Target Tabs: CSV vs Google Sheets */}
          {!googleExportResult && (
            <div className="flex p-1 bg-layer-1-page rounded-xl border border-border-default">
              <button
                type="button"
                onClick={() => setExportTarget('csv')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[13px] sm:text-[14px] font-semibold transition-all ${
                  exportTarget === 'csv'
                    ? 'bg-white text-text-primary shadow-xs'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                <span>📄 Download CSV</span>
              </button>
              {isGwConnected && (
                <button
                  type="button"
                  onClick={() => setExportTarget('google_sheets')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[13px] sm:text-[14px] font-semibold transition-all ${
                    exportTarget === 'google_sheets'
                      ? 'bg-white text-text-primary shadow-xs'
                      : 'text-text-muted hover:text-text-primary'
                  }`}
                >
                  <GoogleSheetsIcon className="w-4 h-4 shrink-0" />
                  <span>Google Sheets</span>
                </button>
              )}
            </div>
          )}

          {!googleExportResult && (
            <div className="flex flex-col gap-3">
              <label className="text-body-sm font-semibold text-text-secondary">
                Additional Options
              </label>
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <div className="relative flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={includeNotes}
                      onChange={(e) => setIncludeNotes(e.target.checked)}
                      className="peer appearance-none w-5 h-5 rounded-md border border-border-default bg-layer-1-page checked:bg-primary checked:border-primary transition-all cursor-pointer hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <svg
                      className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span className="text-body font-medium text-text-primary group-hover:text-primary transition-colors">
                    Include Notes
                  </span>
                </label>

                <label className="flex items-center gap-2.5 cursor-pointer group">
                  <div className="relative flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={includeFollowups}
                      onChange={(e) => setIncludeFollowups(e.target.checked)}
                      className="peer appearance-none w-5 h-5 rounded-md border border-border-default bg-layer-1-page checked:bg-primary checked:border-primary transition-all cursor-pointer hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <svg
                      className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <span className="text-body font-medium text-text-primary group-hover:text-primary transition-colors">
                    Include Followups
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* TARGET 2: Google Sheets Export */}
          {isGwConnected && exportTarget === 'google_sheets' && (
            <div className="flex flex-col gap-4">
              {/* Not connected state */}
              {!isGwConnected && !isGwStatusLoading && (
                <div className="flex flex-col items-center justify-center p-6 bg-blue-50/50 border border-blue-100 rounded-2xl text-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white shadow-xs border border-blue-100 flex items-center justify-center">
                    <GoogleSheetsIcon className="w-7 h-7 shrink-0" />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-semibold text-gray-900">
                      Connect Google Workspace
                    </h3>
                    <p className="text-[13px] text-gray-600 mt-1 leading-relaxed">
                      Connect your Google Workspace account once to export
                      contacts directly into your Google Spreadsheets.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={isConnectingGoogle}
                    onClick={handleConnectGoogle}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[#4285F4] hover:bg-[#3367d6] text-white text-[13px] font-semibold shadow-sm transition-all focus:outline-none disabled:opacity-60"
                  >
                    {isConnectingGoogle
                      ? 'Connecting...'
                      : 'Sign in with Google →'}
                  </button>
                </div>
              )}

              {/* Connected state & form */}
              {isGwConnected && !googleExportResult && (
                <div className="flex flex-col gap-4">
                  {apiErrorMessage && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-[12px] text-red-700">
                      {apiErrorMessage}
                    </div>
                  )}

                  {/* Mode radio options: Case 1 (New) vs Case 2 (Existing) */}
                  <div className="flex flex-col gap-2.5">
                    <label className="text-[13px] font-semibold text-[#1F2241]">
                      Spreadsheet Destination
                    </label>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <label
                        className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                          sheetMode === 'new'
                            ? 'border-green-500 bg-green-50/40 text-gray-900 shadow-xs'
                            : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="sheetMode"
                          checked={sheetMode === 'new'}
                          onChange={() => setSheetMode('new')}
                          className="mt-0.5 text-green-600 focus:ring-green-500"
                        />
                        <div className="flex flex-col">
                          <span className="text-[13px] font-semibold">
                            Create New Spreadsheet
                          </span>
                          <span className="text-[11px] text-gray-500">
                            Generates a brand new Google Sheet in your Drive.
                          </span>
                        </div>
                      </label>

                      <label
                        className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                          sheetMode === 'existing'
                            ? 'border-green-500 bg-green-50/40 text-gray-900 shadow-xs'
                            : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="sheetMode"
                          checked={sheetMode === 'existing'}
                          onChange={() => setSheetMode('existing')}
                          className="mt-0.5 text-green-600 focus:ring-green-500"
                        />
                        <div className="flex flex-col">
                          <span className="text-[13px] font-semibold">
                            Add to Existing Sheet
                          </span>
                          <span className="text-[11px] text-gray-500">
                            Writes / appends to an existing spreadsheet.
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Mode 1: New Sheet Inputs */}
                  {sheetMode === 'new' && (
                    <div className="flex flex-col gap-3 p-3.5 bg-gray-50/70 border border-gray-200 rounded-xl">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[12px] font-semibold text-gray-700">
                          Spreadsheet Title
                        </label>
                        <input
                          type="text"
                          value={sheetTitle}
                          onChange={(e) => setSheetTitle(e.target.value)}
                          placeholder="e.g. Leads Export - September 2026"
                          disabled={isGoogleExporting}
                          className="w-full px-3 py-2 text-[13px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                        />
                      </div>
                    </div>
                  )}

                  {sheetMode === 'existing' && (
                    <div className="p-3.5 bg-gray-50/70 border border-gray-200 rounded-xl">
                      <p className="text-[12px] text-gray-600 leading-relaxed">
                        Choose a native Google Sheet. Uploaded Excel files stay
                        hidden. Export writes to the Sheet1 tab, then opens the
                        spreadsheet.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Success Result State */}
              {googleExportResult && (
                <div className="p-5 bg-green-50/60 border border-green-200 rounded-2xl flex flex-col items-center justify-center text-center gap-4 animate-in fade-in duration-300">
                  <div className="w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xl font-bold">
                    ✓
                  </div>
                  <div>
                    <h3 className="text-[16px] font-bold text-gray-900">
                      Export to Google Sheets Complete!
                    </h3>
                    <p className="text-[13px] text-gray-600 mt-1">
                      <strong>{googleExportResult.row_count ?? 0}</strong>{' '}
                      contacts successfully written to{' '}
                      <strong className="text-gray-900">
                        {googleExportResult.title || 'Google Spreadsheet'}
                      </strong>
                      .
                    </p>
                  </div>

                  {googleExportResult.spreadsheet_url && (
                    <button
                      type="button"
                      onClick={() =>
                        openSpreadsheetUrl(googleExportResult.spreadsheet_url)
                      }
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#50AB3D] hover:bg-[#2F8E29] text-white text-[14px] font-semibold shadow-sm transition-all"
                    >
                      <GoogleSheetsIcon className="w-4 h-4 brightness-200" />
                      <span>Open in Google Sheets</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-border-default bg-layer-1-page flex justify-end gap-3 rounded-b-2xl shrink-0">
          {googleExportResult ? (
            <Button
              type="button"
              variant="primary"
              onClick={onClose}
              className="h-10 px-5 text-white text-body font-medium transition-colors"
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isExportActive}
                className="h-10 px-5 bg-layer-1-page border border-primary text-primary hover:bg-layer-7-hover text-body font-medium transition-colors"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleExport}
                disabled={
                  isExportActive ||
                  (exportTarget === 'google_sheets' && !canSubmitGoogle)
                }
                className="h-10 px-5 text-white text-body font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isExportActive ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>
                      {exportTarget === 'google_sheets'
                        ? 'Exporting to Sheets...'
                        : 'Exporting...'}
                    </span>
                  </>
                ) : exportTarget === 'google_sheets' ? (
                  sheetMode === 'existing' ? (
                    isPickerLoading ? (
                      'Opening Google Picker...'
                    ) : (
                      'Choose spreadsheet'
                    )
                  ) : (
                    'Create new spreadsheet'
                  )
                ) : (
                  'Export CSV'
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default CRMExportModal;
